import { useMutation } from "@tanstack/react-query";
import { useContext } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  AlertBannerContext,
  API_RESPONSE_CODE,
  HandledGraphQLMutationError,
  SUPPLIER_CREATE_MODAL_CONFLICT_CODES
} from "@kivunova/kivufrontendcommon";
import { fetchGraphQL } from "../../Graphql/utils";
import {
  createSupplierQuery,
  updateSupplierQuery,
  addStoreSupplierQuery,
  removeStoreSupplierQuery,
  searchSuppliersQuery,
  deleteSupplierQuery,
  getSupplierListQuery
} from "../../Graphql/querries/supplierQuery";
import {
  setSupplierList,
  addSupplier,
  updateSupplier,
  deleteSupplier
} from "../../store/slices/supplierSlice";
import { Supplier } from "../../components/Suppliers/types/supplier";
import {
  ALERT_BANNER_CODE,
  ALERT_BANNER_CODE_SEVERITY
} from "@kivunova/kivufrontendcommon";
import { RootState } from "../../store/store";
import {
  SearchSuppliersInput,
  SearchSuppliersResponse,
  CreateSupplierInput,
  CreateSupplierResponse,
  UpdateSupplierInput,
  UpdateSupplierResponse,
  AddStoreSupplierInput,
  AddStoreSupplierResponse,
  RemoveStoreSupplierInput,
  RemoveStoreSupplierResponse,
  DeleteSupplierInput,
  DeleteSupplierResponse
} from "../../components/Suppliers/types/supplier";

import {
  LOG_LEVEL,
  buildLogContext,
  captureAppException,
  sendAppLog
} from "../../Utils/appLogger";

/**
 * useGetSupplierList
 * Get all suppliers for a company
 */
export const useGetSupplierList = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const dispatch = useDispatch();
  const userDetails = useSelector((state: RootState) => state.user.userDetails);

  return useMutation<Supplier[], Error, { storeId?: string | null } | void>({
    mutationKey: ["supplierList", userDetails?.companyId],

    mutationFn: async (vars) => {
      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;
      const storeId = userDetails?.storeId;

      sendAppLog(
        LOG_LEVEL.INFO,
        "Started fetching supplier list",
        buildLogContext(
          "suppliers",
          "get-supplier-list",
          userId,
          companyId,
          storeId
        )
      );

      const filterSid =
        vars &&
        typeof vars === "object" &&
        vars.storeId != null &&
        String(vars.storeId).trim() !== ""
          ? String(vars.storeId).trim()
          : undefined;

      const response: {
        listAllSuppliers: { code?: string | null; suppliers: Supplier[] };
      } = await fetchGraphQL(
        getSupplierListQuery,
        filterSid != null ? { storeId: filterSid } : {},
        setAlert
      );

      const listResult = response?.listAllSuppliers;

      // ❌ Unexpected response shape
      if (!listResult) {
        captureAppException(
          "Get supplier list failed: missing listAllSuppliers",
          new Error("Get supplier list failed: missing listAllSuppliers"),
          buildLogContext(
            "suppliers",
            "get-supplier-list",
            userId,
            companyId,
            storeId
          )
        );

        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.INTERNAL_ERROR
        });

        throw new Error("Get supplier list failed: missing listAllSuppliers");
      }

      const { suppliers } = listResult;

      // ❌ Logically broken success response
      if (suppliers == null) {
        captureAppException(
          "Get supplier list failed: missing suppliers on listAllSuppliers",
          new Error(
            "Get supplier list failed: missing suppliers on listAllSuppliers"
          ),
          buildLogContext(
            "suppliers",
            "get-supplier-list",
            userId,
            companyId,
            storeId
          )
        );

        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.INTERNAL_ERROR
        });

        throw new Error(
          "Get supplier list failed: missing suppliers on listAllSuppliers"
        );
      }

      // ✅ Success
      dispatch(setSupplierList(suppliers));

      sendAppLog(
        LOG_LEVEL.INFO,
        "Supplier list fetched successfully",
        buildLogContext(
          "suppliers",
          "get-supplier-list",
          userId,
          companyId,
          storeId,
          { supplierCount: suppliers.length }
        )
      );

      return suppliers;
    },

    onError: (error) => {
      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;
      const storeId = userDetails?.storeId;

      // ❌ Unexpected runtime/network error (errors not already handled in mutationFn)
      captureAppException(
        "Failed to fetch supplier list",
        error,
        buildLogContext(
          "suppliers",
          "get-supplier-list",
          userId,
          companyId,
          storeId
        )
      );

      setAlert({
        severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
        code: ALERT_BANNER_CODE.INTERNAL_ERROR
      });
    }
  });
};

/**
 * useSearchSuppliers
 * Search and fetch list of suppliers
 */
export const useSearchSuppliers = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const dispatch = useDispatch();
  const userDetails = useSelector((state: RootState) => state.user.userDetails);

  return useMutation<Supplier[], Error, SearchSuppliersInput | undefined>({
    mutationKey: ["searchSuppliers"],

    mutationFn: async (mutationInput) => {
      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;
      const storeId = userDetails?.storeId;

      sendAppLog(
        LOG_LEVEL.INFO,
        "Started searching suppliers",
        buildLogContext(
          "suppliers",
          "search-suppliers",
          userId,
          companyId,
          storeId,
          // searchTerm may be undefined if no filter is applied
          ...(mutationInput?.name
            ? [{ searchTerm: String(mutationInput.name) }]
            : [])
        )
      );

      const response: { searchSuppliers: SearchSuppliersResponse } =
        await fetchGraphQL(
          searchSuppliersQuery,
          { input: mutationInput || {} },
          setAlert
        );

      const searchResult = response?.searchSuppliers;

      // ❌ Unexpected response shape
      if (!searchResult) {
        captureAppException(
          "Search suppliers failed: missing searchSuppliers",
          new Error("Search suppliers failed: missing searchSuppliers"),
          buildLogContext(
            "suppliers",
            "search-suppliers",
            userId,
            companyId,
            storeId
          )
        );

        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.INTERNAL_ERROR
        });

        throw new Error("Search suppliers failed: missing searchSuppliers");
      }

      const { suppliers } = searchResult;

      // ❌ Logically broken success response
      if (!suppliers) {
        captureAppException(
          "API returned response but suppliers data missing",
          new Error("API returned response but suppliers data missing"),
          buildLogContext(
            "suppliers",
            "search-suppliers",
            userId,
            companyId,
            storeId
          )
        );

        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.INTERNAL_ERROR
        });

        throw new Error("API returned response but suppliers data missing");
      }

      // ✅ Success
      dispatch(setSupplierList(suppliers));

      sendAppLog(
        LOG_LEVEL.INFO,
        "Suppliers searched successfully",
        buildLogContext(
          "suppliers",
          "search-suppliers",
          userId,
          companyId,
          storeId,
          { supplierCount: suppliers.length }
        )
      );

      return suppliers;
    },

    onError: (error) => {
      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;
      const storeId = userDetails?.storeId;

      // ❌ Unexpected runtime/network error (errors not already handled in mutationFn)
      captureAppException(
        "Failed to search suppliers",
        error,
        buildLogContext(
          "suppliers",
          "search-suppliers",
          userId,
          companyId,
          storeId
        )
      );

      setAlert({
        severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
        code: ALERT_BANNER_CODE.INTERNAL_ERROR
      });
    }
  });
};

/**
 * useCreateSupplier
 * Create a new supplier
 */
export const useCreateSupplier = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const dispatch = useDispatch();
  const userDetails = useSelector((state: RootState) => state.user.userDetails);

  return useMutation<
    { createSupplier: CreateSupplierResponse },
    Error,
    CreateSupplierInput
  >({
    mutationKey: ["createSupplier"],

    mutationFn: async (input) => {
      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;
      const storeId = userDetails?.storeId;

      sendAppLog(
        LOG_LEVEL.INFO,
        "Started creating supplier",
        buildLogContext(
          "suppliers",
          "create-supplier",
          userId,
          companyId,
          storeId,
          { supplierName: String(input.name) }
        )
      );

      const response: { createSupplier?: CreateSupplierResponse } =
        await fetchGraphQL(createSupplierQuery, { input }, setAlert);
      const createResult = response?.createSupplier;

      // ❌ Unexpected response shape
      if (!createResult) {
        captureAppException(
          "Create supplier failed: missing createSupplier",
          new Error("Create supplier failed: missing createSupplier"),
          buildLogContext(
            "suppliers",
            "create-supplier",
            userId,
            companyId,
            storeId,
            { supplierName: String(input.name) }
          )
        );
        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.INTERNAL_ERROR
        });
        throw new HandledGraphQLMutationError(
          "Create supplier: missing createSupplier payload"
        );
      }

      // ⚠️ Expected API/business failure
      if (createResult.code !== API_RESPONSE_CODE.OK) {
        sendAppLog(
          LOG_LEVEL.WARNING,
          "Supplier creation rejected by API",
          buildLogContext(
            "suppliers",
            "create-supplier",
            userId,
            companyId,
            storeId,
            {
              apiCode: String(createResult.code),
              supplierName: String(input.name)
            }
          )
        );
        if (!SUPPLIER_CREATE_MODAL_CONFLICT_CODES.has(createResult.code)) {
          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: createResult.code as unknown as ALERT_BANNER_CODE
          });
        }
        throw new HandledGraphQLMutationError(
          `Supplier creation rejected by API: ${createResult.code}`,
          createResult.code
        );
      }

      const s = createResult.supplier;

      // ❌ Logically broken success response
      if (!s?.id) {
        captureAppException(
          "API returned OK but supplier data missing",
          new Error(
            "API returned OK but supplier data missing after createSupplier"
          ),
          buildLogContext(
            "suppliers",
            "create-supplier",
            userId,
            companyId,
            storeId,
            { supplierName: String(input.name) }
          )
        );
        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.INTERNAL_ERROR
        });
        throw new HandledGraphQLMutationError(
          "API returned OK but supplier data missing after createSupplier"
        );
      }

      return response as { createSupplier: CreateSupplierResponse };
    },

    onSuccess: (response) => {
      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;
      const storeId = userDetails?.storeId;
      const s = response.createSupplier!.supplier!;

      const newSupplier: Supplier = {
        id: s.id,
        name: s.name,
        email: s.email || undefined,
        phone: s.phone || undefined,
        address: s.address || undefined,
        status: s.status
      };

      dispatch(addSupplier(newSupplier));

      // ✅ Success
      sendAppLog(
        LOG_LEVEL.INFO,
        "Supplier created successfully",
        buildLogContext(
          "suppliers",
          "create-supplier",
          userId,
          companyId,
          storeId,
          { supplierId: String(newSupplier.id) }
        )
      );

      setAlert({
        severity: ALERT_BANNER_CODE_SEVERITY.SUCCESS,
        code: ALERT_BANNER_CODE.OK
      });
    },

    onError: (error) => {
      if (error instanceof HandledGraphQLMutationError) {
        return;
      }

      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;
      const storeId = userDetails?.storeId;

      // ❌ Unexpected runtime/network error
      captureAppException(
        "Failed to create supplier",
        error,
        buildLogContext(
          "suppliers",
          "create-supplier",
          userId,
          companyId,
          storeId
        )
      );

      setAlert({
        severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
        code: ALERT_BANNER_CODE.INTERNAL_ERROR
      });
    }
  });
};

/**
 * useUpdateSupplier
 * Update existing supplier information
 */
export const useUpdateSupplier = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const dispatch = useDispatch();
  const userDetails = useSelector((state: RootState) => state.user.userDetails);

  return useMutation<
    { updateSupplier: UpdateSupplierResponse },
    Error,
    UpdateSupplierInput
  >({
    mutationKey: ["updateSupplier"],

    mutationFn: async (input) => {
      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;
      const storeId = userDetails?.storeId;

      sendAppLog(
        LOG_LEVEL.INFO,
        "Started updating supplier",
        buildLogContext(
          "suppliers",
          "update-supplier",
          userId,
          companyId,
          storeId,
          { supplierId: String(input.supplierId) }
        )
      );

      return fetchGraphQL(updateSupplierQuery, { input }, setAlert);
    },

    onSuccess: (response, input) => {
      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;
      const storeId = userDetails?.storeId;
      const updateResult = response?.updateSupplier;

      // ❌ Unexpected response shape
      if (!updateResult) {
        captureAppException(
          "Update supplier failed: missing updateSupplier",
          new Error("Update supplier failed: missing updateSupplier"),
          buildLogContext(
            "suppliers",
            "update-supplier",
            userId,
            companyId,
            storeId,
            { supplierId: String(input.supplierId) }
          )
        );

        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.INTERNAL_ERROR
        });
        return;
      }

      const s = updateResult.supplier;

      // ❌ Logically broken success response
      if (!s?.id) {
        captureAppException(
          "Update supplier failed: missing supplier in response",
          new Error("Update supplier failed: missing supplier in response"),
          buildLogContext(
            "suppliers",
            "update-supplier",
            userId,
            companyId,
            storeId,
            {
              supplierId: String(input.supplierId),
              apiCode: String(updateResult.code)
            }
          )
        );
        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.INTERNAL_ERROR
        });
        return;
      }

      const updatedSupplier: Supplier = {
        id: s.id,
        name: s.name,
        email: s.email || undefined,
        phone: s.phone || undefined,
        address: s.address || undefined,
        status: s.status
      };

      // ✅ Success
      dispatch(updateSupplier(updatedSupplier));

      sendAppLog(
        LOG_LEVEL.INFO,
        "Supplier updated successfully",
        buildLogContext(
          "suppliers",
          "update-supplier",
          userId,
          companyId,
          storeId,
          { supplierId: String(input.supplierId) }
        )
      );

      setAlert({
        severity: ALERT_BANNER_CODE_SEVERITY.SUCCESS,
        code: ALERT_BANNER_CODE.OK
      });
    },

    onError: (error, input) => {
      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;
      const storeId = userDetails?.storeId;

      // ❌ Unexpected runtime/network error
      captureAppException(
        "Failed to update supplier",
        error,
        buildLogContext(
          "suppliers",
          "update-supplier",
          userId,
          companyId,
          storeId,
          { supplierId: String(input?.supplierId) }
        )
      );

      setAlert({
        severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
        code: ALERT_BANNER_CODE.INTERNAL_ERROR
      });
    }
  });
};

/**
 * useDeleteSupplier
 * Delete a supplier (soft delete by changing status)
 */
export const useDeleteSupplier = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const dispatch = useDispatch();
  const userDetails = useSelector((state: RootState) => state.user.userDetails);

  return useMutation<{ deleteSupplier: DeleteSupplierResponse }, Error, string>(
    {
      mutationKey: ["deleteSupplier"],

      mutationFn: async (supplierId: string) => {
        const userId = userDetails?.id;
        const companyId = userDetails?.companyId;
        const storeId = userDetails?.storeId;

        sendAppLog(
          LOG_LEVEL.INFO,
          "Started deleting supplier",
          buildLogContext(
            "suppliers",
            "delete-supplier",
            userId,
            companyId,
            storeId,
            { supplierId }
          )
        );

        const input: DeleteSupplierInput = { supplierId };
        return fetchGraphQL(deleteSupplierQuery, { input }, setAlert);
      },

      onSuccess: (response, supplierId) => {
        const userId = userDetails?.id;
        const companyId = userDetails?.companyId;
        const storeId = userDetails?.storeId;
        const deleteResult = response?.deleteSupplier;

        // ❌ Unexpected response shape
        if (!deleteResult) {
          captureAppException(
            "Delete supplier failed: missing deleteSupplier",
            new Error("Delete supplier failed: missing deleteSupplier"),
            buildLogContext(
              "suppliers",
              "delete-supplier",
              userId,
              companyId,
              storeId,
              { supplierId }
            )
          );

          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: ALERT_BANNER_CODE.INTERNAL_ERROR
          });
          return;
        }

        // ✅ Success
        dispatch(deleteSupplier(supplierId));

        sendAppLog(
          LOG_LEVEL.INFO,
          "Supplier deleted successfully",
          buildLogContext(
            "suppliers",
            "delete-supplier",
            userId,
            companyId,
            storeId,
            { supplierId }
          )
        );

        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.SUCCESS,
          code: ALERT_BANNER_CODE.OK
        });
      },

      onError: (error, supplierId) => {
        const userId = userDetails?.id;
        const companyId = userDetails?.companyId;
        const storeId = userDetails?.storeId;

        // ❌ Unexpected runtime/network error
        captureAppException(
          "Failed to delete supplier",
          error,
          buildLogContext(
            "suppliers",
            "delete-supplier",
            userId,
            companyId,
            storeId,
            { supplierId }
          )
        );

        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.INTERNAL_ERROR
        });
      }
    }
  );
};

/**
 * useAddStoreSupplier
 * Add supplier to store
 */
export const useAddStoreSupplier = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const userDetails = useSelector((state: RootState) => state.user.userDetails);

  return useMutation<
    { addStoreSupplier: AddStoreSupplierResponse },
    Error,
    AddStoreSupplierInput
  >({
    mutationKey: ["addStoreSupplier"],

    mutationFn: async (input) => {
      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;
      const storeId = userDetails?.storeId;

      sendAppLog(
        LOG_LEVEL.INFO,
        "Started adding supplier to store",
        buildLogContext(
          "suppliers",
          "add-store-supplier",
          userId,
          companyId,
          storeId,
          {
            supplierId: String(input.supplierId),
            targetStoreId: String(input.storeId)
          }
        )
      );

      return fetchGraphQL(addStoreSupplierQuery, { input }, setAlert);
    },

    onSuccess: (response, input) => {
      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;
      const storeId = userDetails?.storeId;
      const addResult = response?.addStoreSupplier;

      // ❌ Unexpected response shape
      if (!addResult) {
        captureAppException(
          "Add store supplier failed: missing addStoreSupplier",
          new Error("Add store supplier failed: missing addStoreSupplier"),
          buildLogContext(
            "suppliers",
            "add-store-supplier",
            userId,
            companyId,
            storeId,
            {
              supplierId: String(input.supplierId),
              targetStoreId: String(input.storeId)
            }
          )
        );

        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.INTERNAL_ERROR
        });
        return;
      }

      // ✅ Success
      sendAppLog(
        LOG_LEVEL.INFO,
        "Supplier added to store successfully",
        buildLogContext(
          "suppliers",
          "add-store-supplier",
          userId,
          companyId,
          storeId,
          {
            supplierId: String(input.supplierId),
            targetStoreId: String(input.storeId)
          }
        )
      );

      setAlert({
        severity: ALERT_BANNER_CODE_SEVERITY.SUCCESS,
        code: ALERT_BANNER_CODE.OK
      });
    },

    onError: (error, input) => {
      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;
      const storeId = userDetails?.storeId;

      // ❌ Unexpected runtime/network error
      captureAppException(
        "Failed to add supplier to store",
        error,
        buildLogContext(
          "suppliers",
          "add-store-supplier",
          userId,
          companyId,
          storeId,
          {
            supplierId: String(input?.supplierId),
            targetStoreId: String(input?.storeId)
          }
        )
      );

      setAlert({
        severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
        code: ALERT_BANNER_CODE.INTERNAL_ERROR
      });
    }
  });
};

/**
 * useRemoveStoreSupplier
 * Remove supplier from store
 */
export const useRemoveStoreSupplier = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const userDetails = useSelector((state: RootState) => state.user.userDetails);

  return useMutation<
    { removeStoreSupplier: RemoveStoreSupplierResponse },
    Error,
    RemoveStoreSupplierInput
  >({
    mutationKey: ["removeStoreSupplier"],

    mutationFn: async (input) => {
      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;
      const storeId = userDetails?.storeId;

      sendAppLog(
        LOG_LEVEL.INFO,
        "Started removing supplier from store",
        buildLogContext(
          "suppliers",
          "remove-store-supplier",
          userId,
          companyId,
          storeId,
          {
            supplierId: String(input.supplierId),
            targetStoreId: String(input.storeId)
          }
        )
      );

      return fetchGraphQL(removeStoreSupplierQuery, { input }, setAlert);
    },

    onSuccess: (response, input) => {
      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;
      const storeId = userDetails?.storeId;
      const removeResult = response?.removeStoreSupplier;

      // ❌ Unexpected response shape
      if (!removeResult) {
        captureAppException(
          "Remove store supplier failed: missing removeStoreSupplier",
          new Error(
            "Remove store supplier failed: missing removeStoreSupplier"
          ),
          buildLogContext(
            "suppliers",
            "remove-store-supplier",
            userId,
            companyId,
            storeId,
            {
              supplierId: String(input.supplierId),
              targetStoreId: String(input.storeId)
            }
          )
        );

        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: ALERT_BANNER_CODE.INTERNAL_ERROR
        });
        return;
      }

      // ✅ Success
      sendAppLog(
        LOG_LEVEL.INFO,
        "Supplier removed from store successfully",
        buildLogContext(
          "suppliers",
          "remove-store-supplier",
          userId,
          companyId,
          storeId,
          {
            supplierId: String(input.supplierId),
            targetStoreId: String(input.storeId)
          }
        )
      );

      setAlert({
        severity: ALERT_BANNER_CODE_SEVERITY.SUCCESS,
        code: ALERT_BANNER_CODE.OK
      });
    },

    onError: (error, input) => {
      const userId = userDetails?.id;
      const companyId = userDetails?.companyId;
      const storeId = userDetails?.storeId;

      // ❌ Unexpected runtime/network error
      captureAppException(
        "Failed to remove supplier from store",
        error,
        buildLogContext(
          "suppliers",
          "remove-store-supplier",
          userId,
          companyId,
          storeId,
          {
            supplierId: String(input?.supplierId),
            targetStoreId: String(input?.storeId)
          }
        )
      );

      setAlert({
        severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
        code: ALERT_BANNER_CODE.INTERNAL_ERROR
      });
    }
  });
};
